/**
 * Store Management Screen Mobile
 * 
 * Mobile version of the store management interface for managers.
 * Optimized for mobile devices with touch-friendly controls.
 * 
 * @module StoreManagementScreenMobile
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  Search,
  RefreshCw,
  MapPin,
  Phone,
  Eye,
  Edit,
  UserCheck,
  Plus,
  Filter,
  Star,
  Globe,
  Download,
  FileSpreadsheet,
  ChevronDown,
  Trash2,
  CheckSquare,
  Square,
  X,
  Database,
  CheckCircle
} from 'lucide-react';
import { useStores } from '../../hooks/useStores';
import { useAuth } from '../../contexts/AuthContext';
import { Store, StoreFilters } from '../../@types/store';
import { ThemeColors, Spacing, Typography, BorderRadius } from '../../styles/ThemeStyles';
import { MobileStoreFilterPanel, MobileStoreFilterOptions, SortOption, getDefaultMobileStoreFilters } from '../../components/common/MobileStoreFilterPanel';
import { collection, getDocs, getDocsFromServer, doc, getDoc, Timestamp, query, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getAssignedStores } from '../../utils/storeAssignment';
import type { Store as StoreType } from '../../@types/store';
import { StoreService } from '../../services/StoreService';
import { CSVExportService } from '../../services/CSVExportService';
import { UserProfile } from '../../@types/user';
import toast from 'react-hot-toast';

/**
 * Styled Components
 */
const Container = styled.div`
  min-height: 100vh;
  background: ${ThemeColors.background.primary};
  padding: ${Spacing.md};
  padding-bottom: 80px;
`;

const Header = styled.div`
  margin-bottom: ${Spacing.lg};
`;

const Title = styled.h1`
  font-size: ${Typography.fontSize.xxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.sm};
`;

const TabContainer = styled.div`
  display: flex;
  gap: ${Spacing.xs};
  margin-bottom: ${Spacing.md};
  border-bottom: 2px solid ${ThemeColors.border.light};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Tab = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm} ${Spacing.md};
  background-color: ${props => props.active ? 'white' : 'transparent'};
  border: none;
  border-bottom: 3px solid ${props => props.active ? ThemeColors.primary.main : 'transparent'};
  cursor: pointer;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${props => props.active ? '600' : '500'};
  color: ${props => props.active ? ThemeColors.primary.main : ThemeColors.text.secondary};
  white-space: nowrap;
  transition: all 0.2s ease;
  margin-bottom: -2px;

  &:active {
    background-color: ${ThemeColors.gray[50]};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const TabBadge = styled.span<{ active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background-color: ${props => props.active ? ThemeColors.primary.main : ThemeColors.gray[200]};
  color: ${props => props.active ? 'white' : ThemeColors.text.secondary};
  border-radius: 10px;
  font-size: ${Typography.fontSize.xs};
  font-weight: 600;
`;

const ToolbarRow = styled.div`
  display: flex;
  gap: ${Spacing.sm};
  margin-bottom: ${Spacing.sm};
`;

const SearchBar = styled.div`
  position: relative;
  flex: 1;

  svg {
    position: absolute;
    left: ${Spacing.sm};
    top: 50%;
    transform: translateY(-50%);
    color: ${ThemeColors.text.secondary};
    width: 18px;
    height: 18px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${Spacing.sm} ${Spacing.sm} ${Spacing.sm} 36px;
  border: 1px solid ${ThemeColors.border.main};
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  background: white;
  height: 40px;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
  }

  &::placeholder {
    color: ${ThemeColors.text.secondary};
  }
`;

const FilterIconButton = styled.button<{ active: boolean; hasFilters: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: ${props => props.active ? ThemeColors.primary.main : 'white'};
  color: ${props => props.active ? 'white' : props.hasFilters ? ThemeColors.primary.main : ThemeColors.text.primary};
  border: 1px solid ${props => props.active || props.hasFilters ? ThemeColors.primary.main : ThemeColors.border.main};
  border-radius: 8px;
  cursor: pointer;
  position: relative;
  flex-shrink: 0;

  &:active {
    opacity: 0.8;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const FilterBadgeSmall = styled.span`
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  background: ${ThemeColors.error.main};
  color: white;
  border-radius: 9px;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ActiveFiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.xs};
  margin-bottom: ${Spacing.md};
`;

const ActiveFilterChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: 4px 8px 4px 12px;
  background: ${ThemeColors.primary.light};
  color: ${ThemeColors.primary.dark};
  border-radius: ${BorderRadius.full};
  font-size: ${Typography.fontSize.xs};
  font-weight: 500;
`;

const ChipRemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background: ${ThemeColors.primary.main};
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  padding: 0;

  svg {
    width: 10px;
    height: 10px;
  }
`;

const ClearAllButton = styled.button`
  padding: 4px 12px;
  background: transparent;
  color: ${ThemeColors.primary.main};
  border: 1px solid ${ThemeColors.primary.main};
  border-radius: ${BorderRadius.full};
  font-size: ${Typography.fontSize.xs};
  font-weight: 500;
  cursor: pointer;

  &:active {
    background: ${ThemeColors.primary.light};
  }
`;

const SortIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.sm};
  background: ${ThemeColors.info.light};
  color: ${ThemeColors.info.dark};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.xs};
  margin-bottom: ${Spacing.sm};
`;

const StoreList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md};
`;

const StoreCard = styled.div<{ selected?: boolean }>`
  background: white;
  border-radius: ${Spacing.md};
  padding: ${Spacing.md};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-left: 3px solid ${props => props.selected ? ThemeColors.primary.main : ThemeColors.gray[300]};
  opacity: ${props => props.selected ? 0.95 : 1};
  position: relative;
`;

const StoreHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: ${Spacing.sm};
  gap: 2px;
`;

const StoreName = styled.div`
  font-weight: ${Typography.fontWeight.semibold};
  color: ${ThemeColors.text.primary};
  font-size: ${Typography.fontSize.md};
  margin-bottom: 4px;
`;

const CustomerNumber = styled.div`
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.secondary};
  font-family: ${Typography.fontFamily.mono};
`;

const StoreInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
  margin-bottom: ${Spacing.md};
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
`;

const InfoValue = styled.span`
  color: ${ThemeColors.text.primary};
`;

const StoreActions = styled.div`
  display: flex;
  gap: ${Spacing.xs};
  padding-top: ${Spacing.sm};
  border-top: 1px solid ${ThemeColors.border.light};
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'default' }>`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm};
  border: none;
  background: ${props => props.variant === 'primary' ? ThemeColors.primary.main : ThemeColors.background.secondary};
  color: ${props => props.variant === 'primary' ? 'white' : ThemeColors.text.primary};
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${Spacing.xxl};
  color: ${ThemeColors.text.secondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${Spacing.xxl};
  color: ${ThemeColors.text.secondary};
`;

const FloatingActionButton = styled.button`
  position: fixed;
  bottom: ${Spacing.xl};
  right: ${Spacing.xl};
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${ThemeColors.primary.main};
  color: white;
  border: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;

  svg {
    width: 24px;
    height: 24px;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const RefreshButton = styled(FloatingActionButton)`
  bottom: calc(${Spacing.xl} + 70px);
  background: white;
  color: ${ThemeColors.primary.main};
  border: 2px solid ${ThemeColors.primary.main};
`;

type TabType = 'assigned' | 'all';

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm} ${Spacing.md};
  background-color: ${ThemeColors.primary.main};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  margin-top: ${Spacing.md};
  width: 100%;

  &:active {
    opacity: 0.8;
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const ExportDropdownContainer = styled.div`
  position: relative;
  width: 100%;
  margin-top: ${Spacing.md};
`;

const ExportDropdown = styled.div<{ show: boolean }>`
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  margin-bottom: 8px;
  background: white;
  border: 1px solid ${ThemeColors.border.main};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  opacity: ${props => props.show ? 1 : 0};
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  transform: ${props => props.show ? 'translateY(0)' : 'translateY(-8px)'};
  transition: all 0.2s ease;
`;

const ExportDropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  width: 100%;
  padding: ${Spacing.sm} ${Spacing.md};
  border: none;
  background: transparent;
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${ThemeColors.background.secondary};
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }

  svg {
    width: 18px;
    height: 18px;
    color: ${ThemeColors.text.secondary};
  }
`;

const ExportDropdownDivider = styled.div`
  height: 1px;
  background: ${ThemeColors.border.light};
  margin: ${Spacing.xs} 0;
`;

const ExportDropdownHeader = styled.div`
  padding: ${Spacing.sm} ${Spacing.md};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.hint};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ExportDropdownSectionLabel = styled.div`
  padding: ${Spacing.xs} ${Spacing.md};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.primary.main};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  background: ${ThemeColors.primary.light};
`;

const SpinningIcon = styled(RefreshCw)`
  @keyframes spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
  animation: spin 1s linear infinite;
`;

const ProgressOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${Spacing.md};
`;

const ProgressCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 28px 24px;
  width: 100%;
  max-width: 340px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  text-align: center;
`;

const ProgressTitle = styled.h3`
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: 0;
`;

const ProgressSubtitle = styled.p`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  margin: 0;
`;

const ProgressBarTrack = styled.div`
  width: 100%;
  height: 8px;
  background: ${ThemeColors.gray[200]};
  border-radius: 999px;
  overflow: hidden;
`;

const ProgressBarFill = styled.div<{ pct: number }>`
  height: 100%;
  width: ${props => props.pct}%;
  background: ${ThemeColors.primary.main};
  border-radius: 999px;
  transition: width 0.3s ease;
`;

const ProgressCount = styled.span`
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.primary.main};
`;

const SelectionButton = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm} ${Spacing.md};
  background-color: ${props => props.active ? ThemeColors.primary.main : 'white'};
  color: ${props => props.active ? 'white' : ThemeColors.text.primary};
  border: 1px solid ${props => props.active ? ThemeColors.primary.main : ThemeColors.border.main};
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  margin-top: ${Spacing.md};
  width: 100%;

  &:active {
    opacity: 0.8;
    transform: scale(0.98);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const BulkActionsBar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${ThemeColors.primary.light};
  padding: ${Spacing.md};
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm};
`;

const BulkActionsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: ${Typography.fontWeight.semibold};
  color: ${ThemeColors.primary.main};
  font-size: ${Typography.fontSize.sm};
`;

const BulkActionsButtons = styled.div`
  display: flex;
  gap: ${Spacing.xs};
`;

const BulkActionButton = styled.button<{ variant?: 'danger' | 'primary' | 'secondary' }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm};
  border: none;
  background: ${props => {
    switch (props.variant) {
      case 'danger': return ThemeColors.error.main;
      case 'primary': return ThemeColors.primary.main;
      default: return 'white';
    }
  }};
  color: ${props => props.variant === 'secondary' ? ThemeColors.text.primary : 'white'};
  border: ${props => props.variant === 'secondary' ? `1px solid ${ThemeColors.border.main}` : 'none'};
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;

  &:active {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const SelectionCheckbox = styled.div<{ checked: boolean }>`
  position: absolute;
  top: ${Spacing.sm};
  right: ${Spacing.sm};
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.checked ? ThemeColors.primary.main : 'white'};
  border: 2px solid ${props => props.checked ? ThemeColors.primary.main : ThemeColors.border.main};
  border-radius: 6px;
  cursor: pointer;
  z-index: 2;

  svg {
    color: white;
    width: 18px;
    height: 18px;
  }
`;

/**
 * Format date for mobile display
 */
const formatDate = (date?: Date | any): string => {
  if (!date) return 'Never';
  const d = date.toDate ? date.toDate() : new Date(date);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Store Management Screen Mobile Component
 */
export const StoreManagementScreenMobile: React.FC = () => {
  const navigate = useNavigate();
  const { user, userRole } = useAuth();
  const { t } = useTranslation();
  const isHandLead = userRole === 'hand-lead';
  
  const {
    stores,
    isLoading,
    fetchStores
  } = useStores();

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<MobileStoreFilterOptions>(getDefaultMobileStoreFilters());
  const [sortOption, setSortOption] = useState<SortOption | undefined>(undefined);
  const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // For search: Load all stores once (like desktop version)
  const [allStoresForSearch, setAllStoresForSearch] = useState<Store[]>([]);
  const [isLoadingAllStores, setIsLoadingAllStores] = useState(false);
  
  // For hand-lead: Tab state and assigned stores
  const [activeTab, setActiveTab] = useState<TabType>('assigned');
  const [assignedStores, setAssignedStores] = useState<Store[]>([]);
  const [isLoadingAssignedStores, setIsLoadingAssignedStores] = useState(false);
  
  // Sales reps for filter (loaded from database)
  const [salesReps, setSalesReps] = useState<Array<{ id: string; name: string; username?: string; salesGroup?: string }>>([]);
  
  // Total stores count
  const [totalStoresCount, setTotalStoresCount] = useState<number>(0);
  
  // Export state
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const exportDropdownRef = useRef<HTMLDivElement>(null);

  // Export progress overlay state
  const [exportProgress, setExportProgress] = useState<{
    visible: boolean;
    phase: 'fetching' | 'building' | 'done';
    loaded: number;
    total: number;
    format: 'CSV' | 'Excel';
  }>({ visible: false, phase: 'fetching', loaded: 0, total: 0, format: 'CSV' });
  
  // Debounce search input to avoid excessive queries
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    // Debounce search - wait 400ms after user stops typing
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  /**
   * Load sales reps for filter
   */
  useEffect(() => {
    const loadSalesReps = async () => {
      try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('role', 'in', ['sales-representative', 'hand-lead']));
        const usersSnapshot = await getDocs(q);
        const repsList = usersSnapshot.docs.map(doc => {
          const data = doc.data() as UserProfile;
          return {
            id: doc.id,
            name: data.displayName || data.firstName || data.email || 'Unknown',
            username: data.username || data.email?.split('@')[0] || '',
            salesGroup: data.salesGroup || ''
          };
        });
        setSalesReps(repsList);
      } catch (error) {
        console.error('Error loading sales reps:', error);
      }
    };
    
    loadSalesReps();
  }, []);

  /**
   * Load total stores count
   */
  useEffect(() => {
    const loadTotalStoresCount = async () => {
      try {
        const storesRef = collection(db, 'Stores');
        const querySnapshot = await getDocs(storesRef);
        setTotalStoresCount(querySnapshot.size);
      } catch (error) {
        console.error('[StoreManagementMobile] Error loading total stores count:', error);
      }
    };
    
    loadTotalStoresCount();
  }, []);

  /**
   * Load assigned stores for hand-lead users
   */
  useEffect(() => {
    const loadAssignedStores = async () => {
      if (!isHandLead || !user?.id) {
        return;
      }

      setIsLoadingAssignedStores(true);
      try {
        const storesRef = collection(db, 'Stores');
        const querySnapshot = await getDocs(storesRef);
        
        const storeTypesData: StoreType[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          storeTypesData.push({
            id: doc.id,
            CustomerNo: data.CustomerNo || '',
            StoreName: data.StoreName || 'Unknown Store',
            Address: data.Address || '',
            City: data.City || '',
            Prov: data.Prov || '',
            PostalCode: data.PostalCode || '',
            Telephone: data.Telephone || '',
            EmailAdddress: data.EmailAdddress || '',
            SalespersonNo: data.SalespersonNo || '',
            salesGroup: data.SalesGroup || '',
            assignedToUserIds: data.assignedToUserIds || [],
            status: 'active',
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
          });
        });

        const userDoc = await getDoc(doc(db, 'users', user.id));
        const userData = userDoc.data();

        const assigned = await getAssignedStores(
          user.id, 
          storeTypesData, 
          userData?.salesGroup,
          userData?.assignedStoreIds
        );
        
        const assignedData: Store[] = assigned.map(s => StoreService.mapDbDataToStore(s.id, {
          CustomerNo: s.CustomerNo,
          StoreName: s.StoreName,
          Address: s.Address,
          City: s.City,
          Prov: s.Prov,
          PostalCode: s.PostalCode,
          Telephone: s.Telephone,
          EmailAdddress: s.EmailAdddress,
          SalespersonNo: s.SalespersonNo,
          SalesGroup: s.salesGroup,
          assignedToUserIds: s.assignedToUserIds,
        }));

        console.log(`Assigned stores for hand-lead user ${user.id}:`, assignedData.length);
        setAssignedStores(assignedData);
      } catch (error) {
        console.error('Error loading assigned stores:', error);
      } finally {
        setIsLoadingAssignedStores(false);
      }
    };

    loadAssignedStores();
  }, [isHandLead, user]);

  /**
   * Load ALL stores when searching (like desktop version)
   * This ensures search works across entire database, not just loaded stores
   */
  useEffect(() => {
    const loadAllStoresForSearch = async () => {
      if (!debouncedSearchTerm?.trim()) {
        // Clear all stores when search is cleared
        setAllStoresForSearch([]);
        return;
      }

      setIsLoadingAllStores(true);
      setAllStoresForSearch([]); // Clear so we don't show stale results while fetching from DB
      try {
        console.log('[StoreManagementMobile] Loading ALL stores for search...');
        const storesRef = collection(db, 'Stores');
        const querySnapshot = await getDocsFromServer(storesRef);
        
        console.log(`[StoreManagementMobile] Loaded ${querySnapshot.docs.length} stores for search`);
        
        // Exclude archived at doc level (handles any field casing) so deleted stores never show
        const docsToInclude = querySnapshot.docs.filter(d => {
          const data = d.data();
          const status = data.status ?? data.Status;
          return status !== 'archived';
        });
        
        const allStores: Store[] = docsToInclude
          .map(doc => StoreService.mapDbDataToStore(doc.id, doc.data()))
          .filter(s => s.status !== 'archived');
        
        setAllStoresForSearch(allStores);
      } catch (error) {
        console.error('[StoreManagementMobile] Error loading all stores for search:', error);
        setAllStoresForSearch([]);
      } finally {
        setIsLoadingAllStores(false);
      }
    };

    loadAllStoresForSearch();
  }, [debouncedSearchTerm]);

  // Filter stores by search term (client-side, like desktop version)
  const filteredBySearch = useMemo(() => {
    if (!debouncedSearchTerm?.trim() || allStoresForSearch.length === 0) {
      return [];
    }

    const query = debouncedSearchTerm.toLowerCase().trim();
    console.log(`[StoreManagementMobile] Filtering ${allStoresForSearch.length} stores for search: "${query}"`);
    
    // Check both mapped fields and raw database fields to catch all variations
    const filtered = allStoresForSearch.filter(store => {
      const rawData = (store as any)._rawData || {};
      
      // Helper to safely check if a value contains the query
      const matches = (value: any): boolean => {
        if (!value) return false;
        return String(value).toLowerCase().includes(query);
      };
      
      return (
        matches(store.customerNo) ||
        matches((store as any).CustomerNo) ||
        matches(store.name) ||
        matches((store as any).StoreName) ||
        matches((store as any).Name) ||
        matches(rawData.StoreName) ||
        matches(rawData.Name) ||
        matches(rawData.name) ||
        matches(store.city) ||
        matches((store as any).City) ||
        matches(rawData.City) ||
        matches(rawData.city) ||
        matches(store.address) ||
        matches((store as any).Address) ||
        matches(rawData.Address) ||
        matches(rawData.address) ||
        matches(store.email) ||
        matches((store as any).Email) ||
        matches(rawData.EmailAdddress) ||
        matches(rawData.EmailAddress) ||
        matches(rawData.Email) ||
        matches(rawData.email) ||
        matches(store.phone) ||
        matches(store.telephone) ||
        matches((store as any).Telephone) ||
        matches((store as any).Phone) ||
        matches(rawData.Telephone) ||
        matches(rawData.Phone) ||
        matches(rawData.phone) ||
        matches(store.salesGroup) ||
        matches((store as any).SalesGroup) ||
        matches(rawData.SalesGroup) ||
        matches(rawData.salesGroup) ||
        matches(store.salespersonNo) ||
        matches((store as any).SalespersonNo) ||
        matches(rawData.SalespersonNo) ||
        matches(rawData.salespersonNo) ||
        matches(store.salesPersonName) ||
        matches((store as any).SalesPersonName) ||
        matches(rawData.SalesPersonName) ||
        matches(rawData.salesPersonName) ||
        matches(store.sortField) ||
        matches((store as any).SortField) ||
        matches(rawData.SortField) ||
        matches(rawData.sortField) ||
        matches(store.state) ||
        matches(store.province) ||
        matches((store as any).Prov) ||
        matches((store as any).Province) ||
        matches(rawData.Prov) ||
        matches(rawData.Province) ||
        matches(rawData.province) ||
        matches(rawData.state) ||
        matches(store.pc) ||
        matches((store as any).PostalCode) ||
        matches((store as any).PC) ||
        matches(rawData.PostalCode) ||
        matches(rawData.PC) ||
        matches(rawData.pc) ||
        matches(store.storeNumber) ||
        matches((store as any).StoreNumber) ||
        matches(rawData.StoreNumber) ||
        matches(rawData.storeNumber)
      );
    });
    
    console.log(`[StoreManagementMobile] Found ${filtered.length} stores matching search: "${query}"`);
    
    return filtered;
  }, [allStoresForSearch, debouncedSearchTerm]);

  // Helper function to apply filters to a store list
  const applyFiltersToStores = useCallback((storeList: Store[]): Store[] => {
    let result = [...storeList];
    
    // Apply basic filters
    if (filters.province) {
      result = result.filter(store => 
        store.state === filters.province || 
        store.province === filters.province || 
        (store as any).Prov === filters.province
      );
    }
    
    if (filters.city) {
      result = result.filter(store => store.city === filters.city);
    }
    
    if (filters.salesGroup) {
      result = result.filter(store => store.salesGroup === filters.salesGroup);
    }
    
    if (filters.sortField) {
      result = result.filter(store => 
        store.sortField === filters.sortField || 
        (store as any).SortField === filters.sortField
      );
    }
    
    return result;
  }, [filters]);

  // Helper function to apply sorting
  const applySorting = useCallback((storeList: Store[]): Store[] => {
    if (!sortOption) return storeList;
    
    const sorted = [...storeList];
    const { field, direction } = sortOption;
    const multiplier = direction === 'asc' ? 1 : -1;
    
    sorted.sort((a, b) => {
      let aVal: any, bVal: any;
      
      switch (field) {
        case 'name':
          aVal = a.name || '';
          bVal = b.name || '';
          break;
        case 'customerNo':
          aVal = a.customerNo || '';
          bVal = b.customerNo || '';
          break;
        case 'city':
          aVal = a.city || '';
          bVal = b.city || '';
          break;
        case 'lastVisit':
          aVal = a.lastVisit ? (a.lastVisit instanceof Date ? a.lastVisit : (a.lastVisit as any).toDate?.() || new Date(0)) : new Date(0);
          bVal = b.lastVisit ? (b.lastVisit instanceof Date ? b.lastVisit : (b.lastVisit as any).toDate?.() || new Date(0)) : new Date(0);
          return (aVal.getTime() - bVal.getTime()) * multiplier;
        case 'notesCount':
          aVal = a.notesCount || 0;
          bVal = b.notesCount || 0;
          return (aVal - bVal) * multiplier;
        case 'salesGroup':
          aVal = a.salesGroup || '';
          bVal = b.salesGroup || '';
          break;
        default:
          aVal = (a as any)[field] || '';
          bVal = (b as any)[field] || '';
      }
      
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return aVal.localeCompare(bVal) * multiplier;
      }
      return 0;
    });
    
    return sorted;
  }, [sortOption]);

  // Extract unique values for filters - use appropriate store list based on tab
  const displayedStores = useMemo(() => {
    let result: Store[] = [];
    
    // If user has a search term, always use search results (from DB) — never fall back to full list
    if (debouncedSearchTerm?.trim()) {
      console.log('[StoreManagementMobile] Using search results:', { count: filteredBySearch.length, query: debouncedSearchTerm });
      result = applyFiltersToStores(filteredBySearch);
      
      // For hand-lead on assigned tab, filter search results to only assigned stores
      if (isHandLead && activeTab === 'assigned') {
        const assignedStoreIds = new Set(assignedStores.map(s => s.id));
        result = result.filter(s => assignedStoreIds.has(s.id));
      }
    } else if (isHandLead) {
      // For hand-lead users, filter by assigned/all stores based on active tab
      if (activeTab === 'assigned') {
        result = applyFiltersToStores(assignedStores);
      } else {
        result = applyFiltersToStores(stores);
      }
    } else {
      // Use regular stores (filtered by server-side filters)
      result = stores;
    }
    
    // Apply sorting
    return applySorting(result);
  }, [stores, assignedStores, isHandLead, activeTab, filteredBySearch, debouncedSearchTerm, applyFiltersToStores, applySorting]);

  // For filter options, use all stores (not just displayed) to show all available options
  const storesForFilterOptions = useMemo(() => {
    // If searching, use allStoresForSearch; otherwise use regular stores
    if (debouncedSearchTerm?.trim() && allStoresForSearch.length > 0) {
      return allStoresForSearch;
    }
    return stores;
  }, [stores, allStoresForSearch, debouncedSearchTerm]);

  const availableProvinces = useMemo(() => {
    return Array.from(new Set(storesForFilterOptions.map(s => s.state || s.province || (s as any).Prov).filter(Boolean))).sort() as string[];
  }, [storesForFilterOptions]);

  const availableCities = useMemo(() => {
    return Array.from(new Set(storesForFilterOptions.map(s => s.city).filter(Boolean))).sort() as string[];
  }, [storesForFilterOptions]);

  const availableSalesGroups = useMemo(() => {
    return Array.from(new Set(storesForFilterOptions.map(s => s.salesGroup).filter(Boolean))).sort() as string[];
  }, [storesForFilterOptions]);

  const availableSortFields = useMemo(() => {
    return Array.from(new Set(storesForFilterOptions.map(s => s.sortField || (s as any).SortField).filter(Boolean))).sort() as string[];
  }, [storesForFilterOptions]);

  /**
   * Load stores on component mount and when filters change
   * Always queries database when filters change (resets pagination)
   * Uses debounced search term to avoid excessive queries
   * 
   * NOTE: When searching, we use allStoresForSearch and filter client-side (like desktop version)
   * When not searching, we use the normal flow with server-side filters
   */
  useEffect(() => {
    // If searching, skip this effect - we'll handle search separately
    if (debouncedSearchTerm?.trim()) {
      return;
    }

    // For hand-lead on assigned tab, skip fetching (we use assignedStores)
    if (isHandLead && activeTab === 'assigned') {
      return;
    }

    const storeFilters: StoreFilters = {};

    if (filters.province) {
      storeFilters.province = filters.province;
    }

    if (filters.city) {
      storeFilters.city = filters.city;
    }

    if (filters.salesGroup) {
      storeFilters.salesGroup = filters.salesGroup;
    }

    if (filters.sortField) {
      storeFilters.sortField = filters.sortField;
    }

    fetchStores(storeFilters, { field: 'name', direction: 'asc' });
  }, [debouncedSearchTerm, filters, fetchStores, isHandLead, activeTab]);

  /**
   * Toggle store selection (defined before handleViewStore which depends on it)
   */
  const handleToggleStoreSelection = useCallback((storeId: string) => {
    setSelectedStoreIds(prev => {
      if (prev.includes(storeId)) {
        return prev.filter(id => id !== storeId);
      } else {
        return [...prev, storeId];
      }
    });
  }, []);

  /**
   * Toggle selection mode
   */
  const handleToggleSelectionMode = useCallback(() => {
    setSelectionMode(!selectionMode);
    if (selectionMode) {
      // Exiting selection mode - clear selections
      setSelectedStoreIds([]);
    }
  }, [selectionMode]);

  /**
   * Handle store view — opens store details in a new tab (or toggles selection when in selection mode)
   */
  const handleViewStore = useCallback((store: Store) => {
    // If in selection mode, toggle selection instead of viewing
    if (selectionMode) {
      handleToggleStoreSelection(store.id);
      return;
    }

    const url = `${window.location.origin}/manager/store-details/${store.id}?tab=store-visit`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [selectionMode, handleToggleStoreSelection]);

  /**
   * Select all displayed stores
   */
  const handleSelectAll = useCallback(() => {
    if (selectedStoreIds.length === displayedStores.length) {
      setSelectedStoreIds([]);
    } else {
      setSelectedStoreIds(displayedStores.map(s => s.id));
    }
  }, [displayedStores, selectedStoreIds]);

  /**
   * Handle refresh (defined before handleBatchDelete which depends on it)
   */
  const handleRefresh = useCallback(() => {
    // Clear notes cache to ensure fresh visit counts
    StoreService.invalidateNotesCache();
    
    // If searching, reload all stores for search (from server, exclude archived)
    if (debouncedSearchTerm?.trim()) {
      const loadAllStoresForSearch = async () => {
        setIsLoadingAllStores(true);
        setAllStoresForSearch([]);
        try {
          const storesRef = collection(db, 'Stores');
          const querySnapshot = await getDocsFromServer(storesRef);
          const docsToInclude = querySnapshot.docs.filter(d => {
            const data = d.data();
            const status = data.status ?? data.Status;
            return status !== 'archived';
          });
          const allStores: Store[] = docsToInclude
            .map(doc => StoreService.mapDbDataToStore(doc.id, doc.data()))
            .filter(s => s.status !== 'archived');
          setAllStoresForSearch(allStores);
        } catch (error) {
          console.error('[StoreManagementMobile] Error refreshing stores for search:', error);
          setAllStoresForSearch([]);
        } finally {
          setIsLoadingAllStores(false);
        }
      };
      loadAllStoresForSearch();
    } else {
      // Otherwise, refresh with current filters
      const storeFilters: StoreFilters = {};
      if (filters.province) {
        storeFilters.province = filters.province;
      }
      if (filters.city) {
        storeFilters.city = filters.city;
      }
      if (filters.salesGroup) {
        storeFilters.salesGroup = filters.salesGroup;
      }
      if (filters.sortField) {
        storeFilters.sortField = filters.sortField;
      }
      fetchStores(storeFilters, { field: 'name', direction: 'asc' });
    }
  }, [debouncedSearchTerm, filters, fetchStores]);

  /**
   * Handle batch delete
   */
  const handleBatchDelete = useCallback(async () => {
    if (!user?.id) return;
    if (selectedStoreIds.length === 0) return;

    const storeCount = selectedStoreIds.length;
    const confirmMessage = `⚠️ WARNING: This will PERMANENTLY DELETE ${storeCount} store${storeCount !== 1 ? 's' : ''} from the database.\n\nThis action CANNOT be undone!\n\nAre you absolutely sure you want to continue?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    // Double confirmation for permanent deletion
    const doubleConfirm = window.confirm(`FINAL CONFIRMATION: You are about to permanently delete ${storeCount} store${storeCount !== 1 ? 's' : ''}. Click OK to proceed.`);
    if (!doubleConfirm) {
      return;
    }

    const reason = prompt('Please provide a reason for deletion:');
    if (reason === null || reason.trim() === '') {
      toast.error('Deletion reason is required');
      return;
    }

    setIsDeleting(true);
    
    try {
      let successCount = 0;
      let failureCount = 0;
      const errors: string[] = [];

      // Delete stores one by one (hard delete - permanently removes from database)
      for (const storeId of selectedStoreIds) {
        try {
          const result = await StoreService.deleteStore(storeId, user.id, true);
          if (result.success) {
            successCount++;
          } else {
            failureCount++;
            errors.push(`Store ${storeId}: ${result.error}`);
          }
        } catch (error) {
          failureCount++;
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          errors.push(`Store ${storeId}: ${errorMessage}`);
        }
      }

      // Clear selection and exit selection mode
      setSelectedStoreIds([]);
      setSelectionMode(false);

      // Show results
      if (failureCount === 0) {
        toast.success(`Successfully permanently deleted ${successCount} store${successCount !== 1 ? 's' : ''}`);
      } else if (successCount === 0) {
        const errorMsg = `Failed to delete all ${failureCount} store${failureCount !== 1 ? 's' : ''}`;
        console.error('Batch delete errors:', errors);
        toast.error(errorMsg);
        alert(`${errorMsg}\n\nErrors:\n${errors.join('\n')}`);
      } else {
        const mixedMsg = `Permanently deleted ${successCount} store${successCount !== 1 ? 's' : ''}, but ${failureCount} failed`;
        console.error('Batch delete errors:', errors);
        toast(mixedMsg);
        alert(`${mixedMsg}\n\nErrors:\n${errors.join('\n')}`);
      }

      // Refresh the store list
      handleRefresh();
    } catch (error) {
      console.error('Batch delete error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Batch delete failed: ${errorMessage}`);
    } finally {
      setIsDeleting(false);
    }
  }, [user, selectedStoreIds, handleRefresh]);

  /**
   * Handle create store action
   * Currently directs managers to use the desktop experience until mobile creation flow is implemented.
   */
  const handleCreateStore = useCallback(() => {
    console.warn('Store creation flow is not yet implemented for mobile.');
    if (typeof window !== 'undefined') {
      window.alert(t('storeManagement.createStoreMobileNotSupported', 'Please use the desktop portal to create a store.'));
    }
  }, [t]);

  /**
   * Handle store edit
   */
  const handleEditStore = useCallback((store: Store, e: React.MouseEvent) => {
    e.stopPropagation();
    const breadcrumbLabel = store.customerNo ?? store.CustomerNo ?? store.storeNumber ?? store.StoreNumber ?? store.name ?? store.StoreName ?? store.id;
    navigate(`/manager/store-details/${store.id}?tab=store-visit&mode=edit`, {
      state: {
        storeId: store.id,
        storeBreadcrumbLabel: String(breadcrumbLabel),
        customerNumber: String(breadcrumbLabel)
      }
    });
  }, [navigate]);

  /**
   * Handle assign sales rep
   */
  const handleAssignSalesRep = useCallback((store: Store, e: React.MouseEvent) => {
    e.stopPropagation();
    console.log('Assign sales rep to:', store.id);
    // This would open a modal or navigate to assignment screen
  }, []);

  const handleFilterApply = useCallback((newFilters: MobileStoreFilterOptions, newSort?: SortOption) => {
    setFilters(newFilters);
    setSortOption(newSort);
  }, []);

  const handleRemoveFilter = useCallback((key: keyof MobileStoreFilterOptions) => {
    setFilters(prev => {
      const updated = { ...prev };
      if (key === 'competitors' || key === 'lowStockCategories' || key === 'visitTypes' || key === 'salesRepIds') {
        (updated as any)[key] = [];
      } else if (key === 'storeLevelRange' || key === 'visitDateRange' || key === 'needsManagerContact' || key === 'hasNotes') {
        (updated as any)[key] = null;
      } else {
        delete (updated as any)[key];
      }
      return updated;
    });
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setFilters(getDefaultMobileStoreFilters());
    setSortOption(undefined);
  }, []);

  const getActiveFilterCount = useCallback(() => {
    let count = 0;
    if (filters.province) count++;
    if (filters.city) count++;
    if (filters.salesGroup) count++;
    if (filters.sortField) count++;
    if (filters.competitors?.length) count += filters.competitors.length;
    if (filters.storeLevelRange) count++;
    if (filters.lowStockCategories?.length) count += filters.lowStockCategories.length;
    if (filters.needsManagerContact !== null && filters.needsManagerContact !== undefined) count++;
    if (filters.hasNotes !== null && filters.hasNotes !== undefined) count++;
    if (filters.visitTypes?.length) count += filters.visitTypes.length;
    if (filters.visitDateRange?.from || filters.visitDateRange?.to) count++;
    if (filters.salesRepIds?.length) count += filters.salesRepIds.length;
    return count;
  }, [filters]);

  // Build active filter chips for display
  const activeFilterChips = useMemo(() => {
    const chips: Array<{ key: string; label: string; filterKey: keyof MobileStoreFilterOptions }> = [];
    
    if (filters.province) chips.push({ key: 'province', label: `Province: ${filters.province}`, filterKey: 'province' });
    if (filters.city) chips.push({ key: 'city', label: `City: ${filters.city}`, filterKey: 'city' });
    if (filters.salesGroup) chips.push({ key: 'salesGroup', label: `Sales Group: ${filters.salesGroup}`, filterKey: 'salesGroup' });
    if (filters.sortField) chips.push({ key: 'sortField', label: `Sort Field: ${filters.sortField}`, filterKey: 'sortField' });
    if (filters.storeLevelRange) chips.push({ key: 'storeLevelRange', label: `Stock: ${filters.storeLevelRange.min}-${filters.storeLevelRange.max}%`, filterKey: 'storeLevelRange' });
    if (filters.needsManagerContact !== null && filters.needsManagerContact !== undefined) {
      chips.push({ key: 'needsManagerContact', label: `Manager Contact: ${filters.needsManagerContact ? 'Yes' : 'No'}`, filterKey: 'needsManagerContact' });
    }
    if (filters.hasNotes !== null && filters.hasNotes !== undefined) {
      chips.push({ key: 'hasNotes', label: `Has Visits: ${filters.hasNotes ? 'Yes' : 'No'}`, filterKey: 'hasNotes' });
    }
    if (filters.visitDateRange?.from || filters.visitDateRange?.to) {
      const from = filters.visitDateRange?.from || '...';
      const to = filters.visitDateRange?.to || '...';
      chips.push({ key: 'visitDateRange', label: `Visit: ${from} to ${to}`, filterKey: 'visitDateRange' });
    }
    filters.competitors?.forEach((c, i) => chips.push({ key: `competitor-${i}`, label: `Competitor: ${c}`, filterKey: 'competitors' }));
    filters.lowStockCategories?.forEach((c, i) => chips.push({ key: `lowStock-${i}`, label: `Low Stock: ${c}`, filterKey: 'lowStockCategories' }));
    filters.visitTypes?.forEach((v, i) => chips.push({ key: `visitType-${i}`, label: `Visit Type: ${v}`, filterKey: 'visitTypes' }));
    filters.salesRepIds?.forEach((id, i) => {
      const rep = salesReps.find(r => r.id === id);
      chips.push({ key: `salesRep-${i}`, label: `Sales Rep: ${rep?.name || id}`, filterKey: 'salesRepIds' });
    });
    
    return chips;
  }, [filters, salesReps]);

  /**
   * Handle export to CSV
   */
  const runExport = useCallback(async (format: 'CSV' | 'Excel', scope: 'all' | 'filtered') => {
    setShowExportDropdown(false);
    setIsExporting(true);
    const dateStr = new Date().toISOString().split('T')[0];

    try {
      let storesToExport: Store[];

      if (scope === 'all') {
        setExportProgress({ visible: true, phase: 'fetching', loaded: 0, total: 0, format });
        storesToExport = await CSVExportService.fetchAllStoresForExport((loaded, total) => {
          setExportProgress(prev => ({ ...prev, loaded, total }));
        });
      } else {
        if (displayedStores.length === 0) {
          toast.error('No stores to export');
          return;
        }
        storesToExport = displayedStores;
      }

      if (storesToExport.length === 0) {
        toast.error('No stores to export');
        return;
      }

      setExportProgress(prev => ({ ...prev, visible: true, phase: 'building', total: storesToExport.length }));

      const suffix = scope === 'all' ? '_all' : '_filtered';
      if (format === 'CSV') {
        await CSVExportService.exportStoresToCSV(storesToExport, {
          filename: `stores_export${suffix}_${dateStr}.csv`
        });
      } else {
        await CSVExportService.exportStoresToXLSX(storesToExport, {
          filename: `stores_export${suffix}_${dateStr}.xls`
        });
      }

      setExportProgress(prev => ({ ...prev, phase: 'done' }));
      setTimeout(() => setExportProgress(prev => ({ ...prev, visible: false })), 1200);

      toast.success(`Exported ${storesToExport.length} store${storesToExport.length !== 1 ? 's' : ''} to ${format}`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export stores. Please try again.');
      setExportProgress(prev => ({ ...prev, visible: false }));
    } finally {
      setIsExporting(false);
    }
  }, [displayedStores]);

  const handleExportCSV = useCallback(() => runExport('CSV', 'filtered'), [runExport]);
  const handleExportExcel = useCallback(() => runExport('Excel', 'filtered'), [runExport]);

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
        setShowExportDropdown(false);
      }
    };
    
    if (showExportDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showExportDropdown]);

  return (
    <Container>
      <Header>
        <Title>Stores</Title>
        
        {/* Tabs for hand-lead users */}
        {isHandLead && (
          <TabContainer>
            <Tab 
              active={activeTab === 'assigned'} 
              onClick={() => setActiveTab('assigned')}
            >
              <Star size={16} />
              My Stores
              <TabBadge active={activeTab === 'assigned'}>
                {assignedStores.length}
              </TabBadge>
            </Tab>
            <Tab 
              active={activeTab === 'all'} 
              onClick={() => setActiveTab('all')}
            >
              <Globe size={16} />
              All Stores
              <TabBadge active={activeTab === 'all'}>
                {totalStoresCount}
              </TabBadge>
            </Tab>
          </TabContainer>
        )}

        {/* Search and Filter Row */}
        <ToolbarRow>
          <SearchBar>
            <Search />
            <SearchInput
              type="text"
              placeholder="Search stores..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBar>
          <FilterIconButton 
            active={isFilterOpen}
            hasFilters={getActiveFilterCount() > 0}
            onClick={() => setIsFilterOpen(true)}
          >
            <Filter />
            {getActiveFilterCount() > 0 && (
              <FilterBadgeSmall>{getActiveFilterCount()}</FilterBadgeSmall>
            )}
          </FilterIconButton>
        </ToolbarRow>

        {/* Sort Indicator */}
        {sortOption && (
          <SortIndicator>
            Sorted by: {sortOption.field} ({sortOption.direction === 'asc' ? 'A→Z' : 'Z→A'})
          </SortIndicator>
        )}

        {/* Active Filter Chips */}
        {activeFilterChips.length > 0 && (
          <ActiveFiltersContainer>
            {activeFilterChips.slice(0, 5).map(chip => (
              <ActiveFilterChip key={chip.key}>
                {chip.label}
                <ChipRemoveButton onClick={() => handleRemoveFilter(chip.filterKey)}>
                  <X />
                </ChipRemoveButton>
              </ActiveFilterChip>
            ))}
            {activeFilterChips.length > 5 && (
              <ActiveFilterChip>
                +{activeFilterChips.length - 5} more
              </ActiveFilterChip>
            )}
            <ClearAllButton onClick={handleClearAllFilters}>
              Clear All
            </ClearAllButton>
          </ActiveFiltersContainer>
        )}

        {/* Export and Selection Row */}
        <ToolbarRow>
          <ExportDropdownContainer ref={exportDropdownRef} style={{ flex: 1 }}>
            <ExportButton 
              onClick={() => setShowExportDropdown(!showExportDropdown)}
              disabled={isExporting}
              style={{ marginTop: 0 }}
            >
              {isExporting ? <SpinningIcon size={18} /> : <Download />}
              Export
              <ChevronDown size={14} style={{ marginLeft: 'auto' }} />
            </ExportButton>
            <ExportDropdown show={showExportDropdown}>
              {/* All Stores — fetches entire Firestore DB */}
              <ExportDropdownSectionLabel>
                All Stores ({totalStoresCount || '—'})
              </ExportDropdownSectionLabel>
              <ExportDropdownItem onClick={() => runExport('CSV', 'all')}>
                <Database />
                Export All as CSV
              </ExportDropdownItem>
              <ExportDropdownItem onClick={() => runExport('Excel', 'all')}>
                <Database />
                Export All as Excel
              </ExportDropdownItem>
            </ExportDropdown>
          </ExportDropdownContainer>

          <SelectionButton 
            active={selectionMode}
            onClick={handleToggleSelectionMode}
            style={{ flex: 1, marginTop: 0 }}
          >
            {selectionMode ? <CheckSquare /> : <Square />}
            {selectionMode ? 'Exit' : 'Select'}
          </SelectionButton>
        </ToolbarRow>

      </Header>

      {(isLoading || (isHandLead && isLoadingAssignedStores) || isLoadingAllStores) ? (
        <LoadingState>Loading stores...</LoadingState>
      ) : displayedStores.length === 0 ? (
        <EmptyState>No stores found</EmptyState>
      ) : (
        <StoreList>
          {displayedStores.map((store) => {
            const isSelected = selectedStoreIds.includes(store.id);
            return (
              <StoreCard 
                key={store.id} 
                onClick={() => handleViewStore(store)}
                selected={isSelected}
              >
                {selectionMode && (
                  <SelectionCheckbox 
                    checked={isSelected}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleStoreSelection(store.id);
                    }}
                  >
                    {isSelected && <CheckSquare />}
                  </SelectionCheckbox>
                )}
                
                <StoreHeader>
                  <div>
                    <StoreName>{store.name}</StoreName>
                    <CustomerNumber>#{store.customerNo}</CustomerNumber>
                  </div>
                </StoreHeader>

                <StoreInfo>
                  <InfoRow>
                    <MapPin />
                    <InfoValue>
                      {store.city || 'N/A'}{store.state && `, ${store.state}`}
                    </InfoValue>
                  </InfoRow>
                  {store.phone && (
                    <InfoRow>
                      <Phone />
                      <InfoValue>{store.phone}</InfoValue>
                    </InfoRow>
                  )}
                  <InfoRow>
                    <UserCheck />
                    <InfoValue>{store.salesPersonName || 'Unassigned'}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <span style={{ fontSize: '12px' }}>Sort Field:</span>
                    <InfoValue>{store.sortField || (store as any).SortField || '-'}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <span style={{ fontSize: '12px' }}>Last Visit:</span>
                    <InfoValue>{formatDate(store.lastVisit)}</InfoValue>
                  </InfoRow>
                </StoreInfo>

                {!selectionMode && (
                  <StoreActions>
                    <ActionButton variant="primary" onClick={(e) => { e.stopPropagation(); handleViewStore(store); }}>
                      <Eye />
                      View
                    </ActionButton>
                    <ActionButton onClick={(e) => handleEditStore(store, e)}>
                      <Edit />
                      Edit
                    </ActionButton>
                    <ActionButton onClick={(e) => handleAssignSalesRep(store, e)}>
                      <UserCheck />
                      Assign
                    </ActionButton>
                  </StoreActions>
                )}
              </StoreCard>
            );
          })}
        </StoreList>
      )}

      {/* Bulk Actions Bar - shown when stores are selected */}
      {selectionMode && selectedStoreIds.length > 0 && (
        <BulkActionsBar>
          <BulkActionsHeader>
            <span>
              {selectedStoreIds.length} store{selectedStoreIds.length !== 1 ? 's' : ''} selected
            </span>
            <button 
              onClick={handleSelectAll}
              style={{ 
                background: 'none', 
                border: 'none', 
                color: ThemeColors.primary.main,
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: Typography.fontSize.sm
              }}
            >
              {selectedStoreIds.length === displayedStores.length ? 'Deselect All' : 'Select All'}
            </button>
          </BulkActionsHeader>
          <BulkActionsButtons>
            <BulkActionButton 
              variant="danger" 
              onClick={handleBatchDelete}
              disabled={isDeleting}
            >
              <Trash2 />
              Delete
            </BulkActionButton>
            <BulkActionButton 
              variant="secondary" 
              onClick={() => {
                setSelectedStoreIds([]);
                setSelectionMode(false);
              }}
            >
              Cancel
            </BulkActionButton>
          </BulkActionsButtons>
        </BulkActionsBar>
      )}

      <RefreshButton onClick={handleRefresh} title="Refresh stores">
        <RefreshCw />
      </RefreshButton>

      <FloatingActionButton onClick={handleCreateStore}>
        <Plus />
      </FloatingActionButton>

      <MobileStoreFilterPanel
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApply={handleFilterApply}
        availableProvinces={availableProvinces}
        availableCities={availableCities}
        availableSalesGroups={availableSalesGroups}
        availableSortFields={availableSortFields}
        salesReps={salesReps}
        currentFilters={filters}
        currentSort={sortOption}
        showAdvancedFilters={true}
      />

      {/* Export Progress Overlay */}
      {exportProgress.visible && (
        <ProgressOverlay>
          <ProgressCard>
            {exportProgress.phase === 'done' ? (
              <CheckCircle size={36} color={ThemeColors.success.main} />
            ) : (
              <SpinningIcon size={36} color={ThemeColors.primary.main} />
            )}
            <ProgressTitle>
              {exportProgress.phase === 'fetching' && 'Fetching Stores…'}
              {exportProgress.phase === 'building' && `Building ${exportProgress.format} File…`}
              {exportProgress.phase === 'done' && 'Export Complete!'}
            </ProgressTitle>
            <ProgressSubtitle>
              {exportProgress.phase === 'fetching' && (
                exportProgress.total > 0 ? 'Loading store data from database' : 'Connecting to database…'
              )}
              {exportProgress.phase === 'building' && 'Enriching records with visit data…'}
              {exportProgress.phase === 'done' && 'Your file has been downloaded.'}
            </ProgressSubtitle>
            {exportProgress.total > 0 && exportProgress.phase !== 'done' && (
              <>
                <ProgressBarTrack>
                  <ProgressBarFill
                    pct={exportProgress.phase === 'building'
                      ? 90
                      : Math.round((exportProgress.loaded / exportProgress.total) * 75)}
                  />
                </ProgressBarTrack>
                <ProgressCount>
                  {exportProgress.phase === 'fetching'
                    ? `${exportProgress.loaded} / ${exportProgress.total} stores`
                    : `${exportProgress.total} stores`}
                </ProgressCount>
              </>
            )}
            {exportProgress.phase === 'done' && (
              <ProgressBarTrack>
                <ProgressBarFill pct={100} />
              </ProgressBarTrack>
            )}
          </ProgressCard>
        </ProgressOverlay>
      )}
    </Container>
  );
};

